import React, { useState, useEffect } from "react";
import {
  Box,
  Divider,
  Typography,
  Button,
  Card,
  TextField,
  InputAdornment,
  Paper,
  Breadcrumbs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "../../assets/images/search.svg";
import AddIcon from "../../assets/images/add_2.svg";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../store/loaderSlice";
import { showSnackbar } from "../../store/snackBarSlice";
import CardComponent from "../../components/cards/Card";
import { 
  getNegotiationWorkspaces, 
  createNegotiationWorkspace, 
  updateNegotiationWorkspace, 
  deleteNegotiationWorkspace 
} from "../../api/negotiations";

interface Negotiation {
  contract_id: string;
  contract_workspace: string;
  status: string;
  file_count: number;
  comments?: string;
}

// Function to remove UCW_\d+ prefix from negotiation workspace names
const removeUCWPrefix = (name: string): string => {
  return name.replace(/^UCW_\d+\s*/, '').replace(/^_+/, '');
};

const NegotiationsList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
  const [allNegotiations, setAllNegotiations] = useState<Negotiation[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newNegotiationName, setNewNegotiationName] = useState("");
  const [newNegotiationComments, setNewNegotiationComments] = useState("");
  const [selectedNegotiationId, setSelectedNegotiationId] = useState<string>("");
  const [selectedNegotiationName, setSelectedNegotiationName] = useState<string>("");
  
  // Fetch negotiations from API
  useEffect(() => {
    const fetchNegotiations = async () => {
      try {
        dispatch(showLoader());
        const response = await getNegotiationWorkspaces();
        if (response && response.negotiations) {
          setAllNegotiations(response.negotiations);
          setNegotiations(response.negotiations);
        }
      } catch (error) {
        console.error("Error fetching negotiations:", error);
        dispatch(showSnackbar({ message: "Failed to load negotiations", type: "error" }));
      } finally {
        dispatch(hideLoader());
        setLoading(false);
      }
    };
    
    fetchNegotiations();
  }, [dispatch]);
  
  // Filter negotiations based on search term
  useEffect(() => {
    if (search.trim() === "") {
      setNegotiations(allNegotiations);
    } else {
      const filtered = allNegotiations.filter(negotiation => 
        negotiation.contract_workspace.toLowerCase().includes(search.toLowerCase())
      );
      setNegotiations(filtered);
    }
  }, [search, allNegotiations]);

  // Handle negotiation card click
  const handleNegotiationClick = (id: string, name: string, fileCount: number) => {
    navigate(`/homepage/negotiations/${id}`);
  };
  
  // Handle negotiation action click (edit, delete, etc.)
  const handleNegotiationAction = async (action: any, id: string, comments: string | undefined, name: string) => {
    setSelectedNegotiationId(id);
    setSelectedNegotiationName(name);
    
    switch (action.id) {
      case "add":
        // Redirect to the management page with the negotiation ID
        navigate(`/homepage/management?workspace=${id}`);
        break;
      case "insight":
        navigate(`/homepage/negotiations/${id}`);
        break;
      case "delete":
        setOpenDeleteDialog(true);
        break;
      case "update":
        // TODO: Implement update functionality
        break;
      default:
        navigate(`/homepage/negotiations/${id}`);
        break;
    }
  };
  
  // Handle delete negotiation
  const handleDelete = async () => {
    try {
      dispatch(showLoader());
      await deleteNegotiationWorkspace(parseInt(selectedNegotiationId));
      dispatch(showSnackbar({ message: "Negotiation deleted successfully", type: "success" }));
      // Refresh the list
      const response = await getNegotiationWorkspaces();
      if (response && response.negotiations) {
        setAllNegotiations(response.negotiations);
        setNegotiations(response.negotiations);
      }
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting negotiation:", error);
      dispatch(showSnackbar({ message: "Failed to delete negotiation", type: "error" }));
    } finally {
      dispatch(hideLoader());
    }
  };
  
  // Open the create negotiation dialog
  const handleOpenCreateDialog = () => {
    setNewNegotiationName("");
    setNewNegotiationComments("");
    setOpenCreateDialog(true);
  };

  // Close the create negotiation dialog
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  // Handle creating a new negotiation
  const handleCreateNegotiation = async () => {
    if (newNegotiationName.trim()) {
      try {
        dispatch(showLoader());
        await createNegotiationWorkspace(
          newNegotiationName, 
          newNegotiationComments.trim() ? newNegotiationComments : undefined
        );
        dispatch(showSnackbar({ message: "Negotiation created successfully", type: "success" }));
        // Refresh the list
        const response = await getNegotiationWorkspaces();
        if (response && response.negotiations) {
          setAllNegotiations(response.negotiations);
          setNegotiations(response.negotiations);
        }
        handleCloseCreateDialog();
      } catch (error) {
        console.error("Error creating negotiation:", error);
        dispatch(showSnackbar({ message: "Failed to create negotiation", type: "error" }));
      } finally {
        dispatch(hideLoader());
      }
    } else {
      dispatch(showSnackbar({ message: "Please enter a negotiation name", type: "warning" }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          Home
        </Link>
        <Typography color="text.primary">Negotiations</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Negotiations
        </Typography>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search negotiations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img src={SearchIcon} alt="search" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 0 }}
        />
      </Paper>

      {/* Negotiations List */}
      <Grid container spacing={2}>
        {/* Add New Negotiation Card */}
        <Grid>
          <Card
            variant="outlined"
            sx={{
              height: "230px",
              width: "300px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              border: 3,
              borderColor: theme.palette.secondary.main,
              cursor: "pointer",
            }}
            onClick={handleOpenCreateDialog}
          >
            <Box textAlign="center">
              <Button
                size="large"
                color="primary"
              >
                <img src={AddIcon} alt="Add" />
              </Button>
              <Typography
                variant="body2"
                sx={{ px: 8, pt: 1 }}
              >
                Click to create a new Negotiation
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Existing Negotiations Cards */}
        {negotiations.map((negotiation) => (
          <Grid key={negotiation.contract_id}>
            <CardComponent
              contract_id={negotiation.contract_id}
              contract_workspace={removeUCWPrefix(negotiation.contract_workspace)}
              status={negotiation.status}
              file_count={negotiation.file_count}
              comments={negotiation.comments}
              onClickCard={handleNegotiationClick}
              onActionClicked={handleNegotiationAction}
            />
          </Grid>
        ))}
      </Grid>

      {/* Create Negotiation Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New Negotiation
          <IconButton
            aria-label="close"
            onClick={handleCloseCreateDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Negotiation Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newNegotiationName}
            onChange={(e) => setNewNegotiationName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            required
          />
          <TextField
            margin="dense"
            id="comments"
            label="Comments (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={newNegotiationComments}
            onChange={(e) => setNewNegotiationComments(e.target.value)}
            placeholder="Enter additional details about this negotiation"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCreateDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCreateNegotiation} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Delete Negotiation
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the negotiation "{removeUCWPrefix(selectedNegotiationName)}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Empty state */}
      {!loading && negotiations.length === 0 && (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography variant="h6" color="text.secondary">
            No negotiations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or create a new negotiation
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NegotiationsList;
